/**
 * @module @repo/global-login-sdk/claims
 * Liest die zentralen Autorisierungs-Claims (`app_access`) aus dem JWT.
 *
 * Hinweis: Das Dekodieren dient AUSSCHLIESSLICH dem UX-Gating im Client
 * (Loading/Denied/Content). Die echte Durchsetzung passiert serverseitig
 * über RLS (`identity.authorize`). Clientseitige Checks niemals als
 * Sicherheitsgrenze behandeln.
 */
import type { Session } from '@supabase/supabase-js';
import { type JwtClaims, type AppAccessMap, JwtClaimsSchema } from '@repo/shared-types';

/** Dekodiert den Payload eines JWT (base64url) — ohne Signaturprüfung. */
function decodeJwtPayload(token: string): unknown {
  const part = token.split('.')[1];
  if (!part || typeof atob !== 'function') return null;
  const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
  try {
    // atob ist global in Browsern und Node ≥ 16 verfügbar.
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    return JSON.parse(json) as unknown;
  } catch {
    return null;
  }
}

/** Parst die relevanten Claims aus der Session (oder `null`). */
export function readClaims(session: Session | null): JwtClaims | null {
  if (!session?.access_token) return null;
  const payload = decodeJwtPayload(session.access_token);
  const parsed = JwtClaimsSchema.safeParse(payload);
  return parsed.success ? parsed.data : null;
}

/** Liefert die komplette `app_access`-Map aus der Session. */
export function getAppAccess(session: Session | null): AppAccessMap {
  return readClaims(session)?.app_access ?? {};
}

/**
 * Liefert die Rolle des Nutzers für eine App oder `null`, wenn nicht
 * freigeschaltet (kein approved Grant).
 */
export function roleForApp(session: Session | null, appSlug: string): string | null {
  return getAppAccess(session)[appSlug] ?? null;
}

/** True, wenn der Nutzer für die App freigeschaltet ist (irgendeine Rolle). */
export function hasAppAccess(session: Session | null, appSlug: string): boolean {
  return roleForApp(session, appSlug) !== null;
}

/** True, wenn der Nutzer plattformweiter Admin ist (globale Rolle `admin`). */
export function isPlatformAdmin(session: Session | null): boolean {
  return readClaims(session)?.role === 'admin' || roleForApp(session, 'admin') === 'admin';
}
